// Real-time remote control (0.2.0), TeamViewer-style: the phone's live screen
// (H.264 via WebCodecs) with pointer, wheel and keyboard input sent back over
// the same socket. Every input still passes the phone's approval broker.

import { MEDIA_SOURCE_SCREEN, type RemoteEvent, type RemoteGlobal, type RemoteQuality } from "@fistos/shared/api";
import {
  Bell,
  ChevronLeft,
  Circle,
  Info,
  Keyboard,
  KeyRound,
  LoaderCircle,
  Lock,
  Maximize,
  Minimize,
  PanelTopOpen,
  ScreenShare,
  ScreenShareOff,
  Square,
  Volume1,
  Volume2,
} from "lucide-solid";
import { createSignal, For, onCleanup, Show, type JSX } from "solid-js";
import { toast } from "somoto";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectPortal, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip";
import { MediaSession, webCodecsSupported, type MediaStats } from "@/lib/media";
import { OfflineNote, Unavailable, type PanelProps } from "./common";

const QUALITIES: { value: RemoteQuality; label: string }[] = [
  { value: "low", label: "Low · 480p" },
  { value: "medium", label: "Medium · 720p" },
  { value: "high", label: "High · 1080p" },
];

/** Browser key → Android KeyEvent keycode, for keys that are not text. */
const KEYCODES: Record<string, number> = {
  Enter: 66,
  Backspace: 67,
  Delete: 112,
  Tab: 61,
  ArrowUp: 19,
  ArrowDown: 20,
  ArrowLeft: 21,
  ArrowRight: 22,
  Home: 122,
  End: 123,
  PageUp: 92,
  PageDown: 93,
};
const KEYCODE_VOLUME_UP = 24;
const KEYCODE_VOLUME_DOWN = 25;

/** Input is dropped (moves only) while this much is queued on the socket. */
const MAX_INPUT_BACKLOG = 64 * 1024;

type State = "idle" | "connecting" | "starting" | "live";

export function RemotePanel(props: PanelProps) {
  const id = () => props.device.id;
  const [quality, setQuality] = createSignal(QUALITIES[1]);
  const [state, setState] = createSignal<State>("idle");
  const [stats, setStats] = createSignal<MediaStats | null>(null);
  const [fps, setFps] = createSignal(0);
  const [display, setDisplay] = createSignal<{ width: number; height: number } | null>(null);
  const [keyboard, setKeyboard] = createSignal(false);
  const [fullscreen, setFullscreen] = createSignal(false);
  let canvas!: HTMLCanvasElement;
  let stage!: HTMLDivElement;
  let media: MediaSession | null = null;

  // ------------------------------------------------------------ session

  const onRemote = (e: RemoteEvent) => {
    if (e.t === "refused") return refused(e.what, e.status, e.message);
    switch (e.state) {
      case "starting":
        setState("starting");
        break;
      case "live":
        if (e.display) setDisplay({ width: e.display.width, height: e.display.height });
        setState("live");
        break;
      case "stopped":
        if (e.message) toast.info(`Remote control ended: ${e.message}`);
        teardown();
        break;
      case "error":
        toast.error(e.message || "The phone could not start the screen stream");
        teardown();
        break;
    }
  };

  let lastRefusal = 0;
  const refused = (what: string, status: string, message: string) => {
    // A refused touch-down makes the rest of its gesture fail too; report once.
    if (Date.now() - lastRefusal < 1500) return;
    lastRefusal = Date.now();
    const why = message || status.toLowerCase().replaceAll("_", " ");
    toast.error(`The phone refused ${what === "touch" ? "a touch" : `the ${what}`}: ${why}`);
  };

  let fpsTimer: ReturnType<typeof setInterval> | undefined;
  const start = () => {
    if (!webCodecsSupported()) return toast.error("This browser cannot decode H.264 (WebCodecs).");
    teardown();
    setState("connecting");
    setStats(null);
    const m = new MediaSession(id(), {
      canvas,
      source: MEDIA_SOURCE_SCREEN,
      onStats: setStats,
      onRemote,
      onOpen: () => m.send({ t: "start", quality: quality().value }),
      onError: (msg) => {
        toast.error(msg);
        teardown();
      },
    });
    media = m;
    m.start();
    let last = 0;
    fpsTimer = setInterval(() => {
      const n = media?.stats.frames ?? 0;
      setFps(n - last);
      last = n;
    }, 1000);
  };

  const teardown = () => {
    endGesture("cancel");
    const m = media;
    media = null;
    if (m) {
      m.send({ t: "stop" });
      m.stop();
    }
    clearInterval(fpsTimer);
    setState("idle");
    setDisplay(null);
    setStats(null);
    setFps(0);
  };

  const changeQuality = (q: (typeof QUALITIES)[number]) => {
    setQuality(q);
    // The server restarts the stream at the new size when we are its only viewer.
    if (state() === "live") media?.send({ t: "start", quality: q.value });
  };

  onCleanup(() => {
    teardown();
    if (document.fullscreenElement === stage) void document.exitFullscreen();
  });

  const live = () => state() === "live" && display() !== null;
  const send = (...a: Parameters<MediaSession["send"]>) => (live() ? (media?.send(...a) ?? false) : false);

  // ------------------------------------------------------------ coordinates

  /** Display size in the stream's current orientation (it follows rotation). */
  const displaySize = (): [number, number] | null => {
    const d = display();
    if (!d?.width || !d.height) return null;
    const frameLandscape = canvas.width > canvas.height;
    return frameLandscape === d.width > d.height ? [d.width, d.height] : [d.height, d.width];
  };

  const toDisplay = (clientX: number, clientY: number) => {
    const size = displaySize();
    if (!size) return null;
    const r = canvas.getBoundingClientRect();
    const fx = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const fy = Math.min(1, Math.max(0, (clientY - r.top) / r.height));
    return { x: Math.round(fx * (size[0] - 1)), y: Math.round(fy * (size[1] - 1)) };
  };

  // ------------------------------------------------------------ pointer

  let pointer: number | null = null;
  let pendingMove: { x: number; y: number } | null = null;
  let lastPos = { x: 0, y: 0 };
  let raf = 0;

  const flushMove = () => {
    raf = 0;
    if (!pendingMove) return;
    // Under backpressure, skip intermediate moves; the gesture survives.
    if ((media?.buffered ?? 0) < MAX_INPUT_BACKLOG) send({ t: "touch", a: "move", ...pendingMove });
    pendingMove = null;
  };

  const endGesture = (a: "up" | "cancel") => {
    if (pointer === null) return;
    if (raf) cancelAnimationFrame(raf);
    if (a === "up") flushMove();
    raf = 0;
    pendingMove = null;
    pointer = null;
    send({ t: "touch", a, ...lastPos });
  };

  const onPointerDown = (e: PointerEvent) => {
    if (!live() || pointer !== null || e.button !== 0 || !e.isPrimary) return;
    const p = toDisplay(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    stage.focus({ preventScroll: true });
    canvas.setPointerCapture(e.pointerId);
    pointer = e.pointerId;
    lastPos = p;
    send({ t: "touch", a: "down", ...p });
  };

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerId !== pointer) return;
    const p = toDisplay(e.clientX, e.clientY);
    if (!p) return;
    lastPos = p;
    pendingMove = p;
    if (!raf) raf = requestAnimationFrame(flushMove);
  };

  const onPointerUp = (e: PointerEvent) => {
    if (e.pointerId !== pointer) return;
    const p = toDisplay(e.clientX, e.clientY);
    if (p) lastPos = p;
    endGesture("up");
  };

  const onPointerCancel = (e: PointerEvent) => {
    if (e.pointerId === pointer) endGesture("cancel");
  };

  // Right click = Back, middle click = Home (TeamViewer-style shortcuts).
  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    if (live()) send({ t: "global", action: "back" });
  };
  const onAuxClick = (e: MouseEvent) => {
    if (e.button !== 1) return;
    e.preventDefault();
    send({ t: "global", action: "home" });
  };

  // ------------------------------------------------------------ wheel → swipe

  let wheel = { dx: 0, dy: 0, at: { x: 0, y: 0 }, timer: 0 as ReturnType<typeof setTimeout> | 0 };
  const onWheel = (e: WheelEvent) => {
    if (!live()) return;
    e.preventDefault();
    const p = toDisplay(e.clientX, e.clientY);
    if (!p || pointer !== null) return;
    const unit = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? canvas.getBoundingClientRect().height : 1;
    if (!wheel.timer) wheel.at = p;
    wheel.dx += (e.shiftKey ? e.deltaY : e.deltaX) * unit;
    wheel.dy += (e.shiftKey ? 0 : e.deltaY) * unit;
    if (!wheel.timer) wheel.timer = setTimeout(fireWheel, 90);
  };
  const fireWheel = () => {
    const size = displaySize();
    const { dx, dy, at } = wheel;
    wheel = { dx: 0, dy: 0, at, timer: 0 };
    if (!size) return;
    // Browser pixels → display pixels, capped to most of the screen.
    const scale = size[1] / canvas.getBoundingClientRect().height;
    const cap = (v: number, max: number) => Math.max(-max, Math.min(max, v));
    const mx = cap(dx * scale, size[0] * 0.45);
    const my = cap(dy * scale, size[1] * 0.45);
    if (Math.abs(mx) < 4 && Math.abs(my) < 4) return;
    const clampX = (v: number) => Math.round(Math.max(0, Math.min(size[0] - 1, v)));
    const clampY = (v: number) => Math.round(Math.max(0, Math.min(size[1] - 1, v)));
    // Wheel down scrolls content down: the finger moves up.
    send({ t: "swipe", x1: at.x, y1: at.y, x2: clampX(at.x - mx), y2: clampY(at.y - my), ms: 220 });
  };

  // ------------------------------------------------------------ keyboard

  let text = "";
  let textTimer: ReturnType<typeof setTimeout> | undefined;
  const flushText = () => {
    clearTimeout(textTimer);
    textTimer = undefined;
    if (text) send({ t: "text", text });
    text = "";
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!live() || e.isComposing || e.target !== stage) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return; // leave browser shortcuts alone (paste is handled below)
    if (e.key === "Escape") {
      e.preventDefault();
      flushText();
      send({ t: "global", action: "back" });
      return;
    }
    const code = KEYCODES[e.key];
    if (code) {
      e.preventDefault();
      flushText();
      send({ t: "key", code });
      return;
    }
    if (e.key.length === 1) {
      e.preventDefault();
      text += e.key;
      if (text.length >= 200) flushText();
      else if (!textTimer) textTimer = setTimeout(flushText, 40);
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    if (!live() || e.target !== stage) return;
    const t = e.clipboardData?.getData("text/plain");
    if (!t) return;
    e.preventDefault();
    flushText();
    for (let i = 0; i < t.length; i += 2000) send({ t: "text", text: t.slice(i, i + 2000) });
  };

  // ------------------------------------------------------------ toolbar

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await stage.requestFullscreen();
    } catch {
      toast.error("Fullscreen is not available here");
    }
  };
  const onFullscreenChange = () => setFullscreen(document.fullscreenElement === stage);
  document.addEventListener("fullscreenchange", onFullscreenChange);
  onCleanup(() => document.removeEventListener("fullscreenchange", onFullscreenChange));

  const nav: { action: RemoteGlobal; label: string; icon: (p: { class?: string }) => JSX.Element }[] = [
    { action: "back", label: "Back (right click, Esc)", icon: ChevronLeft },
    { action: "home", label: "Home (middle click)", icon: Circle },
    { action: "recents", label: "Recent apps", icon: Square },
  ];
  const extra: { label: string; icon: (p: { class?: string }) => JSX.Element; run: () => void }[] = [
    { label: "Notifications", icon: Bell, run: () => send({ t: "global", action: "notifications" }) },
    { label: "Quick settings", icon: PanelTopOpen, run: () => send({ t: "global", action: "quick_settings" }) },
    { label: "Volume down", icon: Volume1, run: () => send({ t: "key", code: KEYCODE_VOLUME_DOWN }) },
    { label: "Volume up", icon: Volume2, run: () => send({ t: "key", code: KEYCODE_VOLUME_UP }) },
    { label: "Lock screen", icon: Lock, run: () => send({ t: "global", action: "lock" }) },
  ];

  const ToolButton = (p: { label: string; icon: (p: { class?: string }) => JSX.Element; onClick: () => void }) => (
    <Tooltip>
      <TooltipTrigger
        as={Button<"button">}
        variant="ghost"
        size="icon-sm"
        aria-label={p.label}
        disabled={!live()}
        onClick={p.onClick}
      >
        <p.icon />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>{p.label}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );

  return (
    <Show
      when={props.caps?.screenStream !== false}
      fallback={
        <Unavailable title="Remote control needs FistOS 0.2.0">
          This phone does not offer a live screen. Update it to FistOS 0.2.0 or later.
        </Unavailable>
      }
    >
      <div class="grid gap-4">
        <Show when={!props.device.online}>
          <OfflineNote device={props.device} />
        </Show>
        <div class="flex flex-wrap items-end gap-2">
          <Select<(typeof QUALITIES)[number]>
            options={QUALITIES}
            optionValue="value"
            optionTextValue="label"
            value={quality()}
            onChange={(q) => q && changeQuality(q)}
            disabled={state() === "connecting" || state() === "starting"}
            itemComponent={(p) => <SelectItem item={p.item}>{p.item.rawValue.label}</SelectItem>}
          >
            <SelectTrigger class="w-40" aria-label="Stream quality">
              <SelectValue<(typeof QUALITIES)[number]>>{(s) => s.selectedOption()?.label}</SelectValue>
            </SelectTrigger>
            <SelectPortal>
              <SelectContent />
            </SelectPortal>
          </Select>
          <Show
            when={state() === "idle"}
            fallback={
              <Button variant="outline" onClick={teardown}>
                <ScreenShareOff /> Disconnect
              </Button>
            }
          >
            <Button onClick={start} disabled={!props.device.online}>
              <ScreenShare /> Connect
            </Button>
          </Show>
          <Button variant="ghost" onClick={() => send({ t: "keyframe" })} disabled={!live()}>
            <KeyRound /> Refresh
          </Button>
          <Button variant="ghost" onClick={toggleFullscreen} disabled={state() === "idle"}>
            <Show when={fullscreen()} fallback={<Maximize />}>
              <Minimize />
            </Show>
            {fullscreen() ? "Exit fullscreen" : "Fullscreen"}
          </Button>
        </div>

        <Card class="overflow-hidden p-0">
          <div
            ref={stage}
            tabIndex={0}
            class="relative flex flex-col bg-black outline-none"
            classList={{ "h-[min(78vh,960px)]": !fullscreen(), "h-screen": fullscreen() }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => setKeyboard(true)}
            onBlur={() => {
              flushText();
              setKeyboard(false);
            }}
          >
            <div class="relative flex min-h-0 flex-1 items-center justify-center p-2">
              <canvas
                ref={canvas}
                class="max-h-full max-w-full cursor-crosshair touch-none select-none"
                classList={{ hidden: !stats()?.frames }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onLostPointerCapture={onPointerCancel}
                onContextMenu={onContextMenu}
                onAuxClick={onAuxClick}
                onMouseDown={(e) => e.button === 1 && e.preventDefault()}
                onWheel={onWheel}
              />
              <Show when={!stats()?.frames}>
                <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-white/60">
                  <Show
                    when={state() === "idle"}
                    fallback={
                      <>
                        <LoaderCircle class="size-6 animate-spin" />
                        {state() === "connecting" ? "Connecting…" : "Waiting for the screen…"}
                      </>
                    }
                  >
                    <ScreenShare class="size-6" /> Not connected
                  </Show>
                </div>
              </Show>
              <Show when={stats()?.frames}>
                <div class="pointer-events-none absolute top-2 left-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-0.5 text-[11px] text-white tabular-nums">
                  <span class="size-1.5 animate-pulse rounded-full bg-red-500" />
                  {stats()!.width}×{stats()!.height} · {fps()} fps
                </div>
                <Show when={keyboard()}>
                  <div class="pointer-events-none absolute top-2 right-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-0.5 text-[11px] text-white">
                    <Keyboard class="size-3.5" /> Keyboard goes to the phone
                  </div>
                </Show>
              </Show>
            </div>
            <div class="flex items-center justify-between gap-1 border-t border-white/10 bg-zinc-950 px-2 py-1 text-white [&_button]:text-white [&_button:hover]:bg-white/10">
              <div class="flex gap-1">
                <For each={extra.slice(0, 2)}>
                  {(x) => <ToolButton label={x.label} icon={x.icon} onClick={x.run} />}
                </For>
              </div>
              <div class="flex gap-4">
                <For each={nav}>
                  {(n) => (
                    <ToolButton label={n.label} icon={n.icon} onClick={() => send({ t: "global", action: n.action })} />
                  )}
                </For>
              </div>
              <div class="flex gap-1">
                <For each={extra.slice(2)}>{(x) => <ToolButton label={x.label} icon={x.icon} onClick={x.run} />}</For>
              </div>
            </div>
          </div>
        </Card>
        <p class="text-muted-foreground flex items-start gap-1.5 text-xs">
          <Info class="mt-px size-3.5 shrink-0" />
          <span>
            Drag to swipe, scroll to scroll, type to enter text. Right click is Back, middle click is Home. Secure
            screens such as password and banking apps appear black. The phone shows no indicator while you are
            connected; the stream stops when you leave this tab. Touches on sensitive buttons may ask for your approval
            first.
          </span>
        </p>
      </div>
    </Show>
  );
}
